import React, { useState, useEffect, useContext } from "react";
import { Box, Typography, Button, Grid, Divider, Icon } from "@mui/material";
import { useTranslation } from "react-i18next";
import ServiceTrainer1 from "../../assets/showcases/trainer/service1.jpg";
import ServiceTrainer2 from "../../assets/showcases/trainer/service2.jpg";
import ServiceBarber1 from "../../assets/showcases/barber/service1.jpg";
import ServiceBarber2 from "../../assets/showcases/barber/service2.jpg";
import ServiceSalon1 from "../../assets/showcases/salon/service1.jpeg";
import ServiceSalon2 from "../../assets/showcases/salon/service2.jpeg";

import { CreatorContext } from "../../context/CreatorContext";
import fontIcons from "../../assets/font-glyphs/icons.json";
import chroma from "chroma-js";

const ShowcaseVouchers = ({
	sector,
	showSmallImage,
	showShadow,
	showDivider,
	showBigImage,
	buttonStyle,
	backgroundColor,
	imageStyle,
	voucherIconType,
	voucherIconName,
	tabStyle,
}) => {
	const { t } = useTranslation();
	const [content, setContent] = useState([]);
	const { creator } = useContext(CreatorContext);

	const getTextColorBasedOnBackground = (bgColor) => {
		const r = parseInt(bgColor.substring(1, 3), 16);
		const g = parseInt(bgColor.substring(3, 5), 16);
		const b = parseInt(bgColor.substring(5, 7), 16);
		const brightness = (r * 299 + g * 587 + b * 114) / 1000;
		return brightness > 128 ? "#000000" : "#FFF";
	};

	const AppointmentTabs = () => {
		return (
			<Box sx={{ width: "100%" }}>
				<Grid container spacing={0} sx={{ textAlign: "center" }}>
					<Grid
						item
						xs={4}
						sx={{
							cursor: "pointer",
							padding: "8px 0",
						}}
					>
						<Typography
							sx={{
								fontSize: "8px",
								color: tabStyle === "tabStyle1" ? "inherit" : creator.color,
								marginBottom: tabStyle === "tabStyle1" ? 0.5 : 0,
							}}
						>
							{t("vouchers.available")}
						</Typography>
						<Box
							sx={{
								borderBottom:
									tabStyle === "tabStyle1"
										? `1px solid ${creator.color}`
										: "none",
							}}
						></Box>
					</Grid>
					<Grid
						item
						xs={4}
						sx={{
							cursor: "pointer",
							padding: "8px 0",
						}}
					>
						<Typography
							sx={{
								fontSize: "8px",
								color: "#888",
							}}
						>
							{t("vouchers.active")}
						</Typography>
					</Grid>
					<Grid
						item
						xs={4}
						sx={{
							cursor: "pointer",
							padding: "8px 0",
						}}
					>
						<Typography
							sx={{
								fontSize: "8px",
								color: "#888",
							}}
						>
							{t("vouchers.past")}
						</Typography>
					</Grid>
				</Grid>
			</Box>
		);
	};

	const Icon = React.memo(({ family, name, ...props }) => (
		<span
			style={{
				fontFamily:
					family === "FontAwesome5"
						? "'FontAwesome5', 'FontAwesome5_Regular', 'FontAwesome5Brands'"
						: family,
				fontSize: "10px",
				color: creator?.whiteTextButton ? "white" : "black",
			}}
			{...props}
		>
			{String.fromCodePoint(fontIcons[family][name])}
		</span>
	));

	useEffect(() => {
		const loadContent = () => {
			switch (sector) {
				case "barber":
					return [
						{
							title: t("vouchers.classicHaircut"),
							price: "€130.00",
							description: t("vouchers.classicHaircutDescription"),
							image: ServiceBarber1,
						},
						{
							title: t("vouchers.beardTrim"),
							price: "€80.00",
							description: t("vouchers.beardTrimDescription"),
							image: ServiceBarber2,
						},
					];

				case "salon":
					return [
						{
							title: t("vouchers.manicurePackage"),
							price: "€40.00",
							description: t("vouchers.manicurePackageDescription"),
							image: ServiceSalon1,
						},
						{
							title: t("vouchers.pedicurePackage"),
							price: "€70.00",
							description: t("vouchers.pedicurePackageDescription"),
							image: ServiceSalon2,
						},
					];

				case "trainer":
					return [
						{
							title: t("vouchers.personalTrainingPackage"),
							price: "€150.00",
							description: t("vouchers.personalTrainingDescription"),
							image: ServiceTrainer1,
						},
						{
							title: t("vouchers.nutritionConsultation"),
							price: "€100.00",
							description: t("vouchers.nutritionConsultationDescription"),
							image: ServiceTrainer2,
						},
					];

				default:
					return [];
			}
		};

		setContent(loadContent());
	}, [sector, t]);

	return (
		<>
			<Box
				sx={{
					display: "flex",
					paddingLeft: 1,
					paddingRight: 1,
					"& *:not(span)": {
						fontFamily: creator.fontFamilyCap + "!important",
					},
				}}
			>
				<AppointmentTabs />
			</Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					padding: 1,
					gap: 1,
					paddingTop: 0,
					"& *:not(span)": {
						fontFamily: creator.fontFamilyCap + "!important",
					},
				}}
			>
				{content.map((voucher, index) => (
					<>
						{showBigImage ? (
							<>
								<Box
									key={index}
									sx={{
										display: "flex",
										flexDirection: "column",
										backgroundColor: backgroundColor,
										color: getTextColorBasedOnBackground(backgroundColor),
										boxShadow: showShadow
											? "0 2px 4px rgba(0, 0, 0, 0.05)"
											: "none",
									}}
								>
									<img
										src={voucher.image}
										alt={voucher.title}
										style={{
											width: "100%",
											height: "70px",
											objectFit: "cover",
										}}
									/>

									<Box sx={{ padding: 1 }}>
										<Typography
											variant="body2"
											sx={{ fontWeight: "bold", fontSize: "8px" }}
										>
											{voucher.title}
										</Typography>
										<Typography
											variant="body2"
											sx={{
												fontWeight: "bold",
												fontSize: "8px",
												marginBottom: "8px",
											}}
										>
											{voucher.price}
										</Typography>
										<Typography
											variant="body2"
											sx={{ fontSize: "8px", margin: "8px 0" }}
										>
											{voucher.description}
										</Typography>

										{buttonStyle === "voucherButtonStyle1" ? (
											<Button
												variant="contained"
												fullWidth
												sx={{
													backgroundColor: creator.color,
													color: creator?.whiteTextButton ? "white" : "black",
													borderRadius: "24px",
													height: "24px",
													fontSize: "8px",
													textTransform: "uppercase",
													fontWeight: "normal",
													width: "100%!important",
													"&:hover": {
														backgroundColor: creator.color,
													},
												}}
											>
												{t("vouchers.buy")}
											</Button>
										) : (
											<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
												<Box
													sx={{
														width: 20,
														height: 20,
														borderRadius: 10,
														backgroundColor: creator.color,
														display: "flex",
														justifyContent: "center",
														alignItems: "center",
													}}
												>
													<Icon
														family={voucherIconType ?? "MaterialCommunityIcons"}
														name={voucherIconName ?? "cart"}
													/>
												</Box>
											</Box>
										)}
									</Box>
								</Box>
								{!showShadow && showDivider && (
									<Divider
										sx={{
											borderColor:
												creator.themeMode === "Dark"
													? chroma(creator.themeColor).brighten(1).hex()
													: chroma(creator.themeColor).darken(0.5).hex(),
										}}
									/>
								)}
							</>
						) : (
							<>
								{" "}
								<Box
									key={index}
									sx={{
										display: "flex",
										flexDirection: "row",
										backgroundColor: backgroundColor,
										color: getTextColorBasedOnBackground(backgroundColor),
										boxShadow: showShadow
											? "0 2px 4px rgba(0, 0, 0, 0.05)"
											: "none",
										padding: 1,
										gap: 1,
									}}
								>
									{showSmallImage && (
										<img
											src={voucher.image}
											alt={voucher.title}
											style={{
												width: "50px",
												height: "50px",
												marginBottom: "8px",
												objectFit: "cover",
											}}
										/>
									)}

									<Box>
										<Typography
											variant="body2"
											sx={{ fontWeight: "bold", fontSize: "8px" }}
										>
											{voucher.title}
										</Typography>
										<Typography
											variant="body2"
											sx={{
												fontWeight: "bold",
												fontSize: "8px",
												marginBottom: "8px",
											}}
										>
											{voucher.price}
										</Typography>
										<Typography
											variant="body2"
											sx={{ fontSize: "8px", margin: "8px 0" }}
										>
											{voucher.description}
										</Typography>

										{buttonStyle === "voucherButtonStyle1" ? (
											<Button
												variant="contained"
												fullWidth
												sx={{
													backgroundColor: creator.color,
													color: creator?.whiteTextButton ? "white" : "black",
													borderRadius: "24px",
													height: "24px",
													fontSize: "8px",
													textTransform: "uppercase",
													fontWeight: "normal",
													width: "100%!important",
													"&:hover": {
														backgroundColor: creator.color,
													},
												}}
											>
												{t("vouchers.buy")}
											</Button>
										) : (
											<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
												<Box
													sx={{
														width: 20,
														height: 20,
														borderRadius: 10,
														backgroundColor: creator.color,
														display: "flex",
														justifyContent: "center",
														alignItems: "center",
													}}
												>
													<Icon
														family={voucherIconType ?? "MaterialCommunityIcons"}
														name={voucherIconName ?? "cart"}
													/>
												</Box>
											</Box>
										)}
									</Box>
								</Box>
								{!showShadow && showDivider && (
									<Divider
										sx={{
											borderColor:
												creator.themeMode === "Dark"
													? chroma(creator.themeColor).brighten(1).hex()
													: chroma(creator.themeColor).darken(0.5).hex(),
										}}
									/>
								)}
							</>
						)}
					</>
				))}
			</Box>
		</>
	);
};

export default ShowcaseVouchers;
